type KendoWidgetCallback<TWidget = unknown> = (widget: TWidget) => void;
type SortDirection = 'asc' | 'desc';

interface KendoGridSortDescriptor
{
    field?: string;
    dir?: SortDirection;
}

interface KendoGridLike
{
    dataSource: {
        sort(): KendoGridSortDescriptor[] | undefined;
        sort(sort: { field: string; dir: SortDirection }): void;
    };
}

interface GridSortingChangedArgs
{
    field: string;
    direction: SortDirection;
}

interface GridSortingOptions
{
    onSortChanged?: (args: GridSortingChangedArgs) => void;
}

interface CustomerPortalKendoUtilities
{
    whenWidget: typeof whenKendoWidget;
    setupGridSorting: typeof setupGridSorting;
}

interface CustomerPortalWindow
{
    kendo?: CustomerPortalKendoUtilities;
    whenKendoWidget?: typeof whenKendoWidget;
}

interface Window
{
    CustomerPortal?: CustomerPortalWindow;
    showErrors?: (errors: string[]) => void;
}

function whenKendoWidget<TWidget = unknown>(
    selector: string,
    widgetName: string,
    callback: KendoWidgetCallback<TWidget>)
{
    const maxAttempts = 50;      // 50 attempts × 20ms = 1 second total
    const pollIntervalMs = 20;

    function waitForWidget(attempt: number)
    {
        const widget = jQuery(selector).data(widgetName) as TWidget | undefined;
        if (widget)
        {
            callback(widget);
            return;
        }

        if (attempt >= maxAttempts)
        {
            window.showErrors?.(['A page component did not finish loading. Please refresh the page and try again.']);
            return;
        }

        setTimeout(function () { waitForWidget(attempt + 1); }, pollIntervalMs);
    }

    waitForWidget(0);
}

function setupGridSorting(gridContainer: JQuery, options: GridSortingOptions = {})
{
    const customSortLinks = gridContainer.find<HTMLElement>('.custom-sort');
    const sortFieldSelect = gridContainer.find<HTMLSelectElement>('.custom-sort-field');
    const sortDirectionButton = gridContainer.find<HTMLButtonElement>('.custom-sort-direction');

    customSortLinks.on('click', function (event)
    {
        event.preventDefault();
        applySort(this.dataset.sortField ?? '', this.dataset.sortType ?? 'alpha');
    });

    sortFieldSelect.on('change', sortControlChanged);
    sortDirectionButton.on('click', sortControlChanged);

    function sortControlChanged()
    {
        const selected = sortFieldSelect.find(':selected');
        applySort(String(sortFieldSelect.val() ?? ''), String(selected.data('sortType') ?? 'alpha'));
    }

    function applySort(sortField: string, sortType: string)
    {
        const grid = gridContainer.data('kendoGrid') as KendoGridLike | undefined;
        if (!grid)
        {
            window.showErrors?.(['A page grid did not finish loading. Please refresh the page and try again.']);
            return;
        }

        const sort = grid.dataSource.sort();
        customSortLinks.children('[data-fa-i2svg], i')
            .removeClass(function () { return 'fa-sort-' + this.parentElement?.dataset.sortType + '-up fa-sort-' + this.parentElement?.dataset.sortType + '-down'; })
            .addClass('d-none')
            .removeAttr('title');
        sortFieldSelect.val(sortField);
        sortDirectionButton.children('[data-fa-i2svg], i')
            .removeClass('fa-sort-alpha-up fa-sort-alpha-down fa-sort-numeric-up fa-sort-numeric-down');

        const direction: SortDirection = sort?.length && sort[0].field === sortField && sort[0].dir === 'asc'
            ? 'desc'
            : 'asc';
        const iconDirection = direction === 'asc' ? 'up' : 'down';
        const title = direction === 'asc' ? 'In ascending order' : 'In descending order';

        customSortLinks.filter('[data-sort-field=' + sortField + ']')
            .children('[data-fa-i2svg], i')
            .addClass('fa-sort-' + sortType + '-' + iconDirection)
            .removeClass('d-none')
            .attr('title', title)
            .attr('data-prefix', 'fa');
        sortDirectionButton
            .attr('title', title)
            .children('[data-fa-i2svg], i')
            .addClass('fa-sort-' + sortType + '-' + iconDirection)
            .attr('data-prefix', 'fa');

        grid.dataSource.sort({ field: sortField, dir: direction });
        options.onSortChanged?.({ field: sortField, direction });
    }
}

window.CustomerPortal ??= {};
window.CustomerPortal.kendo = {
    whenWidget: whenKendoWidget,
    setupGridSorting
};
window.CustomerPortal.whenKendoWidget = whenKendoWidget;
